import { all, put, select, takeEvery, takeLatest } from "redux-saga/effects";
import { isActionOf } from "typesafe-actions";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import MapTool from "@mapmycustomers/shared/enum/map/MapTool";
import LongLat from "@mapmycustomers/shared/types/base/LongLat";
import { Lead, MapEntity } from "@mapmycustomers/shared/types/entity";
import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import {
  CategorizedMapEntries,
  Cluster,
  EntityPin,
  MapEntitiesSet,
  MapRecordsResponse,
  MultiPin,
} from "@mapmycustomers/shared/types/map";
import Organization from "@mapmycustomers/shared/types/Organization";
import RangeModel from "@mapmycustomers/shared/types/viewModel/internalModel/RangeModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import MapViewState from "@mapmycustomers/shared/types/viewModel/MapViewState";
import PlatformFilterModel from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";
import {
  convertLatLngLiteralToLongLat,
  convertPinRegionToLongLat,
  convertPlatformBoundsToLatLngBounds,
} from "@mapmycustomers/shared/util/geo/GeoService";

import { callApi } from "@app/store/api/callApi";
import { hideEntityView } from "@app/store/entityView/actions";
import { handleError } from "@app/store/errors/actions";
import { getOrganizationId } from "@app/store/iam";
import GeoPath from "@app/types/GeoPath";
import RulerPath from "@app/types/map/RulerPath";
import { getRulerPinId } from "@app/util/map/getRulerPinId";
import { mapEntityIdGetter } from "@app/util/map/idGetters";
import { mapEntityIdParser } from "@app/util/map/idParsers";
import isValidMapEntry from "@app/util/map/isValidMapEntry";
import convertMapFilterToPlatformFilterModel from "@app/util/viewModel/convertMapFilterToPlatformFilterModel";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";

import {
  addToRouteSelection,
  appendRouteLassoPath,
  cleanRouteLassoPaths,
  clearRouteSelection,
  enterRouteLassoMode,
  exitRouteLassoMode,
  fetchRouteLassoRecords,
  fetchRouteLassoSelection,
  refreshRouteLassoMode,
  removeFromRouteSelection,
  restartRouteSelection,
  setRouteLassoContainsFlags,
  setRouteRulerPath,
  setRouteSelection,
  setRouteSelectionEntities,
  setRouteTool,
  toggleRouteRulerPin,
  updateRouteLassoClusters,
} from "../../actions";
import {
  getRouteCategorizedExternalMapEntries,
  getRouteCategorizedMapEntries,
  getRouteEditorStops,
  getRouteExcludedRecords,
  getRouteIncludedRecords,
  getRouteLassoPaths,
  getRouteMultiPinRecordsRange,
  getRouteRecordsListSearch,
  getRouteRecordsListSortOrder,
  getRouteRulerPath,
  getRouteSelectedEntities,
  getRouteSelectedItems,
  getRouteTool,
  getRouteViewState,
} from "../../selectors";
import { EditorRouteStop } from "../../types/RouteStop";
import { MULTI_PIN_RECORDS_PAGE_SIZE } from "../../utils/constants";

// Emit an empty lasso selection: clear the selection AND reset the records list, so
// the sidebar doesn't keep showing a stale count. fetchRouteLassoRecords returns an
// empty page once selection is empty. Used on every "nothing selectable" exit.
function* putEmptyRouteLassoSelection() {
  yield put(clearRouteSelection());
  yield put(
    fetchRouteLassoRecords.request({ range: { endRow: MULTI_PIN_RECORDS_PAGE_SIZE, startRow: 0 } })
  );
  yield put(fetchRouteLassoSelection.success());
}

// Ensure entities[entityType].$and is an array, then append the given platform
// filter conditions onto it. Shared by the lasso sagas' id/name filter injections.
const appendEntityAndConditions = (
  entities: Record<EntityTypesSupportedByMapsPage, PlatformFilterModel>,
  entityType: EntityTypesSupportedByMapsPage,
  ...conditions: NonNullable<PlatformFilterModel["$and"]>
): void => {
  if (!("$and" in entities[entityType])) {
    entities[entityType].$and = [];
  }
  const andCondition = entities[entityType].$and;
  if (Array.isArray(andCondition)) {
    andCondition.push(...conditions);
  }
};

export function* onRouteToolChanged({ payload }: ReturnType<typeof setRouteTool>) {
  // Compare previous tool to current; but setRouteTool doesn't carry previous tool.
  // Same approach as Map's onSelectMapTool: act based on the *new* value.
  // Leaving RULER (new tool is anything other than RULER): clear rulerPath.
  if (payload !== MapTool.RULER) {
    const currentPath: RulerPath = yield select(getRouteRulerPath);
    if (currentPath.length > 0) {
      yield put(setRouteRulerPath([]));
    }
  }
  // LASSO lifecycle
  if (payload === MapTool.LASSO) {
    yield put(hideEntityView());
    yield put(enterRouteLassoMode());
  } else {
    // Detect "leaving LASSO" via paths length as a proxy (only set during a lasso session).
    const paths: GeoPath[] = yield select(getRouteLassoPaths);
    if (paths.length > 0) {
      yield put(exitRouteLassoMode());
    }
  }
}

export function* onEnterRouteLassoMode() {
  try {
    yield put(restartRouteSelection());
    yield put(cleanRouteLassoPaths());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onExitRouteLassoMode() {
  try {
    yield put(cleanRouteLassoPaths());
    yield put(restartRouteSelection());
    yield put(setRouteTool(undefined));
    // Re-run the now path-less selection so the records list clears too (not just
    // selected.*). Mirrors the map's onExitLassoMode, which refreshes after clearing —
    // without it the sidebar keeps showing the previous selection on lasso re-entry.
    yield put(refreshRouteLassoMode());
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onRefreshRouteLassoMode() {
  try {
    yield put(fetchRouteLassoSelection.request({ request: {} }));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onAppendRouteLassoPath() {
  try {
    const mapEntries: CategorizedMapEntries = yield select(getRouteCategorizedMapEntries);
    const externalMapEntries: CategorizedMapEntries = yield select(
      getRouteCategorizedExternalMapEntries
    );
    const combinedClusters: Cluster[] = [
      ...(mapEntries.clusters ?? []),
      ...(externalMapEntries.clusters ?? []),
    ];
    yield put(updateRouteLassoClusters(combinedClusters));
    yield put(fetchRouteLassoSelection.request({ request: {} }));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onFetchRouteLassoSelection({
  payload,
}: ReturnType<typeof fetchRouteLassoSelection.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: MapViewState = yield select(getRouteViewState);
    const activeTool: MapTool | undefined = yield select(getRouteTool);
    const lassoPaths: GeoPath[] = yield select(getRouteLassoPaths);
    const sortOrder: SortModel = yield select(getRouteRecordsListSortOrder);

    const isLassoMode = activeTool === MapTool.LASSO;
    const hasLasso = isLassoMode && lassoPaths.length;

    if (!hasLasso) {
      yield* putEmptyRouteLassoSelection();
      return;
    }

    // Unscoped — reused by the included-records refetch below so every explicitly
    // included id resolves, keeping selected.items and selected.entities in sync.
    const mapFilters = convertMapFilterToPlatformFilterModel(
      viewState.filter,
      viewState.visibleEntities
    );

    // The lasso may only select pins CURRENTLY DISPLAYED on the map. With "show other
    // pins" OFF, the only displayed pins are the route's own stops (true in Create and
    // Edit alike — the stops live in the editor either way, with no saved route to scope
    // by), so restrict the polygon sweep to those stops' ids. With the toggle ON,
    // off-route pins are shown too, so the sweep stays unscoped.
    let polygonFilters = mapFilters;
    if (!viewState.showOther) {
      const stops: ReadonlyArray<EditorRouteStop> = yield select(getRouteEditorStops);
      const stopIdsByType: Record<
        EntityType.COMPANY | EntityType.PERSON,
        Array<MapEntity["id"]>
      > = { [EntityType.COMPANY]: [], [EntityType.PERSON]: [] };
      for (const stop of stops) {
        if (stop.entity === EntityType.COMPANY && stop.accountId != null) {
          stopIdsByType[EntityType.COMPANY].push(stop.accountId);
        } else if (stop.entity === EntityType.PERSON && stop.contactId != null) {
          stopIdsByType[EntityType.PERSON].push(stop.contactId);
        }
      }

      const displayedTypes = ([EntityType.COMPANY, EntityType.PERSON] as const).filter(
        (type) => stopIdsByType[type].length > 0
      );

      // Nothing displayed on the map → nothing is selectable.
      if (displayedTypes.length === 0) {
        yield* putEmptyRouteLassoSelection();
        return;
      }

      polygonFilters = convertMapFilterToPlatformFilterModel(viewState.filter, displayedTypes);
      const entities = polygonFilters.entities as Record<
        EntityTypesSupportedByMapsPage,
        PlatformFilterModel
      >;
      for (const type of displayedTypes) {
        appendEntityAndConditions(entities, type, { id: { $in: stopIdsByType[type] } });
      }
    }

    const multipolygon: LongLat[][] = payload.request.lasso
      ? payload.request.lasso
      : lassoPaths.map((path) =>
          path.map((item: google.maps.LatLng): LongLat => [item.lng(), item.lat()])
        );

    const requestPayload = {
      $filters: {
        ...polygonFilters,
        includeAccessStatus: true,
        includeAssociations: true,
        includeCustomFields: true,
        includeEmailSubscriptionStatus: true,
        multipolygon,
        skipAccessibleCount: true,
      },
      $limit: 10000,
      $offset: 0,
      $order: sortOrder ? convertToPlatformSortModel(sortOrder) : undefined,
    };

    const response: MapRecordsResponse = yield callApi("fetchMapPins", orgId, requestPayload);
    const responseItems = response.data ?? [];

    const excluded: MapEntitiesSet = yield select(getRouteExcludedRecords);
    const included: MapEntitiesSet = yield select(getRouteIncludedRecords);

    const itemsWithoutExcluded = new Set(
      responseItems
        .map((entity: MapEntity) => mapEntityIdGetter(entity))
        .filter((value: string) => !excluded.has(value))
    );
    const itemsWithIncluded = new Set([
      ...Array.from(included),
      ...Array.from(itemsWithoutExcluded),
    ]);
    yield put(setRouteSelection(itemsWithIncluded));

    const selectionEntities = responseItems.filter(
      (entity: MapEntity) => !excluded.has(mapEntityIdGetter(entity))
    );

    if (included.size) {
      // Fetch full data for included IDs not in the response (mirrors Map's second BE call)
      const entityIdFilter: Array<MapEntity["id"]> = [];
      for (const mapEntityId of included.values()) {
        const parsed = mapEntityIdParser(mapEntityId);
        if (parsed) {
          entityIdFilter.push(parsed.id);
        }
      }

      if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
        const entities = mapFilters.entities as Record<
          EntityTypesSupportedByMapsPage,
          PlatformFilterModel
        >;
        (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((key) => {
          appendEntityAndConditions(entities, key, { id: { $in: entityIdFilter } });
        });
      }

      const secondResponse: MapRecordsResponse = yield callApi("fetchMapPins", orgId, {
        $filters: { ...mapFilters, skipAccessibleCount: true },
        $limit: 10000,
        $offset: 0,
      });
      yield put(setRouteSelectionEntities([...selectionEntities, ...(secondResponse.data ?? [])]));
    } else {
      yield put(setRouteSelectionEntities(selectionEntities));
    }

    // Kick off the paginated records fetch so the sidebar's first page loads
    // (mirror Map's flow where onFetchLassoSelection populates the selection
    // set and fetchRecords populates the visible page).
    yield put(
      fetchRouteLassoRecords.request({
        range: { endRow: MULTI_PIN_RECORDS_PAGE_SIZE, startRow: 0 },
      })
    );

    yield put(fetchRouteLassoSelection.success());
  } catch (error) {
    yield put(fetchRouteLassoSelection.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchRouteLassoRecords({
  payload,
}: ReturnType<typeof fetchRouteLassoRecords.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const viewState: MapViewState = yield select(getRouteViewState);
    const activeTool: MapTool | undefined = yield select(getRouteTool);
    const lassoPaths: GeoPath[] = yield select(getRouteLassoPaths);
    const selectedItems: MapEntitiesSet = yield select(getRouteSelectedItems);
    const range: RangeModel = payload.range ?? (yield select(getRouteMultiPinRecordsRange));
    const search: string | undefined = payload.search ?? (yield select(getRouteRecordsListSearch));
    const sortOrder: SortModel = yield select(getRouteRecordsListSortOrder);

    const isLassoMode = activeTool === MapTool.LASSO;
    if (!isLassoMode || lassoPaths.length === 0) {
      yield put(fetchRouteLassoRecords.success({ records: [], totalRecords: 0 }));
      return;
    }

    // Mirror Map's onFetchRecords: nothing selected -> nothing to fetch. Without
    // this the BE would return every pin in the polygon (count = polygon size),
    // and pagination text would say e.g. "1—2 of 1234" when only 2 are selected.
    if (selectedItems.size === 0) {
      yield put(fetchRouteLassoRecords.success({ records: [], totalRecords: 0 }));
      return;
    }

    const mapFilters = convertMapFilterToPlatformFilterModel(
      viewState.filter,
      viewState.visibleEntities
    );

    const searchFilter = search?.trim();

    // Inject `id: { $in: [...selectedItems] }` per entity type so the BE returns
    // only the currently-selected records, not every pin in the lasso polygon.
    // Mirrors mapMode/sagas.ts onFetchRecords lasso branch.
    if (mapFilters.entities && !Array.isArray(mapFilters.entities)) {
      const entityIdFilter: Record<EntityTypesSupportedByMapsPage, Array<MapEntity["id"]>> = {
        [EntityType.COMPANY]: [],
        [EntityType.DEAL]: [],
        [EntityType.PERSON]: [],
      };
      for (const mapEntityId of selectedItems.values()) {
        const parsed = mapEntityIdParser(mapEntityId);
        if (parsed) {
          entityIdFilter[parsed.entity as EntityTypesSupportedByMapsPage].push(parsed.id);
        }
      }
      const entities = mapFilters.entities as Record<
        EntityTypesSupportedByMapsPage,
        PlatformFilterModel
      >;
      (Object.keys(entities) as EntityTypesSupportedByMapsPage[]).forEach((entityKey) => {
        if (viewState.visibleEntities.includes(entityKey)) {
          appendEntityAndConditions(entities, entityKey, {
            id: { $in: entityIdFilter[entityKey] },
          });
          if (searchFilter?.length) {
            appendEntityAndConditions(entities, entityKey, { name: { $in: searchFilter } });
          }
        }
      });
    }

    const requestPayload = {
      $filters: {
        ...mapFilters,
        includeAccessStatus: true,
        includeAssociations: true,
        includeCustomFields: true,
        includeEmailSubscriptionStatus: true,
      },
      $limit: range.endRow - range.startRow,
      $offset: range.startRow,
      $order: sortOrder ? convertToPlatformSortModel(sortOrder) : undefined,
    };

    const response: ListResponse<MapEntity> = yield callApi("fetchPins", orgId, requestPayload);

    yield put(
      fetchRouteLassoRecords.success({
        records: response.data ?? [],
        totalRecords: response.total ?? selectedItems.size,
      })
    );
  } catch (error) {
    yield put(fetchRouteLassoRecords.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onUpdateRouteSelection(
  action: ReturnType<typeof addToRouteSelection> | ReturnType<typeof removeFromRouteSelection>
) {
  try {
    // Mirrors Map's onUpdateSelection (mapMode/sagas.ts:703-780) minus the
    // records-list / sidebar visibility / lasso-cluster updates — Route's
    // sidebar reads selected.entities directly and the cluster banner has
    // its own update path. We still need to sync selected.items so the
    // RouteEntityActions Add/Remove toggle flips immediately, and we skip
    // the BE refetch on the remove path since no new data is needed.
    if (!action.payload) {
      return;
    }

    const isRemove = isActionOf(removeFromRouteSelection, action);
    const selectedItems: MapEntitiesSet = yield select(getRouteSelectedItems);
    const payload =
      action.payload instanceof Set ? action.payload : new Set<string>([action.payload]);

    const newItems = isRemove
      ? new Set<string>(Array.from(selectedItems).filter((value) => !payload.has(value)))
      : new Set<string>([...payload, ...selectedItems]);

    yield put(setRouteSelection(newItems));

    if (isRemove) {
      const entities: MapEntity[] = yield select(getRouteSelectedEntities);
      yield put(
        setRouteSelectionEntities(
          entities.filter((entity) => newItems.has(mapEntityIdGetter(entity)))
        )
      );
      // Refresh the paginated records list so the sidebar reflects the new
      // selection size / first page contents. Skips the full lasso refetch
      // (selection state is already correct locally).
      yield put(
        fetchRouteLassoRecords.request({
          range: { endRow: MULTI_PIN_RECORDS_PAGE_SIZE, startRow: 0 },
        })
      );
    } else {
      yield put(refreshRouteLassoMode());
    }
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onUpdateRouteLassoClusters({
  payload,
}: ReturnType<typeof updateRouteLassoClusters>) {
  try {
    const activeTool: MapTool | undefined = yield select(getRouteTool);
    const isLassoMode = activeTool === MapTool.LASSO;
    let containsClusters = false;
    if (isLassoMode && payload && payload.length > 0) {
      const paths: GeoPath[] = yield select(getRouteLassoPaths);
      if (paths.length > 0) {
        const area = new google.maps.Polygon({ paths });
        const bounds = new google.maps.LatLngBounds();
        area
          .getPath()
          .forEach((point) => bounds.extend(new google.maps.LatLng(point.lat(), point.lng())));
        const matches = payload
          .filter((cluster) => isValidMapEntry(cluster) && cluster.bounds)
          .filter((cluster) => {
            // we just ensured above that cluster.bounds is defined
            const clusterBounds = convertPlatformBoundsToLatLngBounds(cluster.bounds!);
            return (
              bounds.intersects(clusterBounds) ||
              google.maps.geometry.poly.containsLocation(
                { lat: cluster.region!.latitude, lng: cluster.region!.longitude },
                area
              )
            );
          });
        containsClusters = matches.length > 0;
      }
    }
    yield put(setRouteLassoContainsFlags({ containsClusters }));
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onToggleRouteRulerPin({ payload }: ReturnType<typeof toggleRouteRulerPin>) {
  const tool: MapTool | undefined = yield select(getRouteTool);
  if (tool !== MapTool.RULER) {
    return;
  }
  const path: RulerPath = yield select(getRouteRulerPath);
  const pinId = getRulerPinId(payload);
  if (pinId === undefined) {
    return;
  }
  const alreadyInPath = path.some((point) => getRulerPinId(point.pin) === pinId);
  if (alreadyInPath) {
    yield put(setRouteRulerPath(path.filter((point) => getRulerPinId(point.pin) !== pinId)));
    return;
  }
  const coordinates =
    "place_id" in payload
      ? convertLatLngLiteralToLongLat((payload as Lead).geometry.location)
      : convertPinRegionToLongLat((payload as EntityPin | MultiPin).region);
  if (!coordinates) {
    return;
  }
  yield put(setRouteRulerPath([...path, { coordinates, pin: payload }]));
}

export function* toolsSaga() {
  yield all([
    takeEvery(setRouteTool, onRouteToolChanged),
    takeLatest(toggleRouteRulerPin, onToggleRouteRulerPin),
    takeLatest(enterRouteLassoMode, onEnterRouteLassoMode),
    takeLatest(exitRouteLassoMode, onExitRouteLassoMode),
    takeLatest(refreshRouteLassoMode, onRefreshRouteLassoMode),
    takeLatest(appendRouteLassoPath, onAppendRouteLassoPath),
    takeLatest(fetchRouteLassoSelection.request, onFetchRouteLassoSelection),
    takeLatest(fetchRouteLassoRecords.request, onFetchRouteLassoRecords),
    takeLatest([addToRouteSelection, removeFromRouteSelection], onUpdateRouteSelection),
    takeLatest(updateRouteLassoClusters, onUpdateRouteLassoClusters),
  ]);
}
